import { FilterMeta, Row, Table } from '@tanstack/react-table';
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils';
import { FilterFn } from '@tanstack/react-table';
import { GTInternalIds } from './types';
import _ from 'lodash';

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

type FuzzyFilter = (
  row: Row<any>,
  columnId: string,
  value: any,
  addMeta: (meta: FilterMeta) => void,
  searchable?: true | string[],
) => boolean;

export const fuzzyFilter: FuzzyFilter = (row, columnId, value, addMeta, searchable) => {
  // If empty value, show all rows
  if (!value || value === '') {
    addMeta({
      itemRank: {
        passed: true,
        rank: 0,
        rankedValue: '',
        accessorIndex: 0,
        accessorThreshold: 0,
      },
    });
    return true;
  }

  let rank: any;

  if (searchable === true) {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  } else if (Array.isArray(searchable)) {
    let anyMatch = false;
    const matchResults = [];

    for (const key of searchable) {
      let fieldValue = row.getValue(key);

      if (fieldValue === undefined) {
        fieldValue = row.original[key];
      }

      if (fieldValue === null || fieldValue === undefined) {
        continue;
      }

      const itemRank = rankItem(fieldValue, value);
      matchResults.push(itemRank);

      if (itemRank.passed) {
        anyMatch = true;
      }
    }

    if (matchResults.length > 0) {
      rank = matchResults.reduce((best, current) => (current.rank < best.rank ? current : best), {
        rank: Number.POSITIVE_INFINITY,
        passed: false,
      });

      rank.passed = anyMatch;
    } else {
      rank = { rank: Number.POSITIVE_INFINITY, passed: false };
    }
  } else {
    rank = rankItem(row.getValue(columnId), value);
  }

  addMeta({ itemRank: rank });
  return rank.passed;
};

export const getClassNameByHeaderId = (headerId: string) => {
  switch (headerId) {
    case GTInternalIds.tdm:
      return 'gt-align-right';
    case GTInternalIds.expander:
      return 'gt-align-left';
    case GTInternalIds.checker:
      return 'gt-align-left';
    default:
      return '';
  }
};

type GTCheckHandler = <T = string>(
  params: { table: Table<any>; selector: string | string[]; checked: T[] },
  cb: (checked: T[]) => void,
) => { checkAllHandler: () => void; isAllChecked: boolean; visibleRows: T[] };

export const gTCheckHandler: GTCheckHandler = ({ table, selector, checked }, setChecked) => {
  const visibleRows = table.getRowModel().rows.map((el) => {
    if (typeof selector === 'string') {
      return el.original[selector];
    } else if (Array.isArray(selector)) {
      const index = selector.findIndex((key) => Boolean(el.original[key]));
      return index >= 0 ? el.original[selector[index]] : el.original[selector[0]];
    }
    return null;
  });

  const isAllChecked = _.every(visibleRows, (element) => _.includes(checked, element));

  const checkAllHandler = () => {
    if (isAllChecked) {
      setChecked(_.difference(checked, visibleRows));
    } else {
      const ids = _.uniq([...checked, ...visibleRows]);
      setChecked(ids);
    }
  };

  return { checkAllHandler, isAllChecked, visibleRows };
};
